import React from 'react';
import { FilterBanner } from './FilterBanner';
import './cardStats.css';

/**
 * Two-tier tabbed chip browser. Parent categories render as a tab strip;
 * only the active tab's sub-chips render below. Selecting a tab doubles
 * as a broad group filter (when `allowGroupSelection` is true). Sub-chip
 * selection overrides the group filter.
 *
 * Selection shape: `{ kind: 'group' | 'chip', key }` or `null`.
 * Caller owns the state; this component is stateless except for the
 * currently-visible tab (auto-syncs from external selection).
 *
 * `groups` shape:
 *   [{
 *     key: 'consistency',
 *     label: 'Consistency',
 *     color: '#4a9bd6',               // optional accent color
 *     items: [{ key, label, count, title? }]
 *   }, ...]
 */
export function CategoryChipBrowser({
    groups,
    selection,
    onSelectionChange,
    title,
    subtitle,
    allowGroupSelection = true,
    activeFilterLabel,
    activeFilterValue,
    activeFilterDetail,
    activeFilterColor,
    activeFilterHelp,
    onClearSelection,
}) {
    const safeGroups = Array.isArray(groups) ? groups : [];

    const findParentKey = React.useCallback(
        (chipKey) => {
            const parent = safeGroups.find((g) =>
                (g.items || []).some((it) => it.key === chipKey)
            );
            return parent?.key;
        },
        [safeGroups]
    );

    const derivedTabKey = React.useMemo(() => {
        if (selection?.kind === 'group') return selection.key;
        if (selection?.kind === 'chip') {
            const parentKey = findParentKey(selection.key);
            if (parentKey) return parentKey;
        }
        return safeGroups[0]?.key;
    }, [selection, safeGroups, findParentKey]);

    const [tabKey, setTabKey] = React.useState(derivedTabKey);
    React.useEffect(() => {
        if (derivedTabKey && derivedTabKey !== tabKey) {
            setTabKey(derivedTabKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [derivedTabKey]);

    if (safeGroups.length === 0) return null;

    const activeGroup = safeGroups.find((g) => g.key === tabKey) || safeGroups[0];
    const showTabBar = safeGroups.some((g) => !!g.label);

    const applySelection = (next) => {
        if (!onSelectionChange) return;
        if (
            selection &&
            next &&
            selection.kind === next.kind &&
            selection.key === next.key
        ) {
            onSelectionChange(null);
            return;
        }
        onSelectionChange(next);
    };

    const handleTabClick = (groupKey) => {
        setTabKey(groupKey);
        if (!allowGroupSelection) return;
        applySelection({ kind: 'group', key: groupKey });
    };

    const handleChipClick = (chipKey) => {
        applySelection({ kind: 'chip', key: chipKey });
    };

    return (
        <div className="statsSection">
            {(title || subtitle) && (
                <div className="statsSectionHeader">
                    {title && <span className="statsSectionTitle">{title}</span>}
                    {subtitle && <span className="statsSectionSubtitle">{subtitle}</span>}
                </div>
            )}
            {showTabBar && (
            <div className="roleTabBar" role="tablist">
                {safeGroups.map((group) => {
                    if (!group.label) return null;
                    const isActiveTab = group.key === (activeGroup?.key || tabKey);
                    const isGroupSelected =
                        selection?.kind === 'group' && selection.key === group.key;
                    const accent = group.color;
                    const style = accent
                        ? isActiveTab || isGroupSelected
                            ? { borderBottomColor: accent, color: accent }
                            : { borderBottomColor: 'transparent' }
                        : undefined;
                    const classes = [
                        'roleTab',
                        isActiveTab ? 'is-active' : '',
                        isGroupSelected ? 'is-selected' : '',
                        !allowGroupSelection ? 'roleTab--view' : '',
                    ]
                        .filter(Boolean)
                        .join(' ');
                    return (
                        <button
                            key={group.key}
                            type="button"
                            role="tab"
                            aria-selected={isActiveTab}
                            className={classes}
                            style={style}
                            onClick={() => handleTabClick(group.key)}
                            title={
                                allowGroupSelection
                                    ? group.groupTitle ||
                                      `Show every item in ${group.label} combined`
                                    : group.label
                            }
                        >
                            <span className="roleTabLabel">{group.label}</span>
                        </button>
                    );
                })}
            </div>
            )}
            {(activeGroup?.items || []).length > 0 && (
                <div className="roleChipRow" role="tabpanel">
                    {activeGroup.items.map((item) => {
                        const isActive =
                            selection?.kind === 'chip' && selection.key === item.key;
                        const accent = activeGroup.color;
                        const chipStyle = accent
                            ? isActive
                                ? { borderLeftColor: accent, color: accent }
                                : { borderLeftColor: accent }
                            : undefined;
                        return (
                            <button
                                key={item.key}
                                type="button"
                                className={`roleChip ${isActive ? 'is-active' : ''}`}
                                onClick={() => handleChipClick(item.key)}
                                style={chipStyle}
                                title={item.title}
                            >
                                <span className="roleChipLabel">{item.label}</span>
                                {item.count != null && (
                                    <span className="roleChipCount">{item.count}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
            {activeFilterValue && onClearSelection && (
                <FilterBanner
                    label={activeFilterLabel}
                    value={activeFilterValue}
                    detail={activeFilterDetail}
                    color={activeFilterColor}
                    onClear={onClearSelection}
                    ariaLabel="Clear selection"
                    help={activeFilterHelp}
                />
            )}
        </div>
    );
}
